import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { formatDateTime } from "../services/datetime";
import { fetchProgramVersion, fetchProgramVersions } from "../services/loyaltyService";
import { labelFor } from "../services/enumLabels";

/** Every setting a version holds, in the order the details list them. */
const SETTINGS = [
  "branch_id",
  "is_active",
  "mode",
  "earn_on",
  "points_per_currency",
  "points_per_visit",
  "service_points",
  "stamps_per_visit",
  "stamps_required",
  "first_visit_bonus",
  "birthday_bonus",
  "referral_bonus",
  "points_expire_after_months",
  "points_expire_after_days",
  "reward_validity_days",
];

const num = (value) => String(Number(value));

/** "1 pt per 1 JOD · 10 stamps = reward · expiry 12 months" — from the version's main rules. */
export function versionSummary(summary, currency, t) {
  if (!summary) return "";
  const parts = [];
  const points = summary.mode !== "stamps";
  const stamps = summary.mode !== "points";

  if (points && Number(summary.points_per_currency) > 0) {
    parts.push(
      t("loyalty.history.summary.perCurrency", {
        points: num(summary.points_per_currency),
        currency: currency?.code ?? t("loyalty.history.summary.unit"),
      })
    );
  }
  if (points && Number(summary.points_per_visit) > 0) {
    parts.push(t("loyalty.history.summary.perVisit", { points: summary.points_per_visit }));
  }
  if (stamps && summary.stamps_required) {
    parts.push(t("loyalty.history.summary.stamps", { count: summary.stamps_required }));
  }
  if (points) {
    parts.push(
      summary.points_expire_after_months
        ? t("loyalty.history.summary.expiryMonths", { count: summary.points_expire_after_months })
        : summary.points_expire_after_days
          ? t("loyalty.history.summary.expiryDays", { count: summary.points_expire_after_days })
          : t("loyalty.history.summary.noExpiry")
    );
  }
  if (summary.is_active === false) parts.push(t("loyalty.history.summary.off"));

  return parts.join(" · ");
}

/** "Branch changed: A → B" / "Copied from A v2" — what a version did besides its settings. */
function VersionEvents({ version }) {
  const { t } = useTranslation();
  const name = (ref) => ref?.name ?? t("loyalty.programs.unknownBranch");

  return (
    <>
      {version.branch_changed && (
        <p className="mt-0.5 text-sm font-medium text-warning-text">
          {t("loyalty.programs.branchChanged", {
            from: name(version.branch_changed.from),
            to: name(version.branch_changed.to),
          })}
        </p>
      )}
      {version.copied_from && (
        <p className="mt-0.5 text-sm text-ink-2">
          {t("loyalty.programs.copiedFrom", {
            branch: name(version.copied_from.branch),
            number: version.copied_from.version_number,
          })}
        </p>
      )}
    </>
  );
}

/**
 * Loyalty → Programs → one programme → History: every saved version of that
 * branch's rules, newest first, each opening a read-only view with what
 * changed highlighted. Versions are never edited; points already earned keep
 * the rules they were earned under. `branchNames` ({id: name}) labels the
 * branch setting.
 */
export default function LoyaltyProgramHistory({ programId, branchNames = {}, refreshKey = 0 }) {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(
    (next) => {
      setLoading(true);
      setError("");
      fetchProgramVersions(programId, { page: next })
        .then(({ versions: rows, meta: m }) => {
          setVersions(rows);
          setMeta(m);
          setPage(next);
        })
        .catch((err) => setError(apiErrorMessage(err, t("loyalty.history.loadFailed"))))
        .finally(() => setLoading(false));
    },
    [programId, t]
  );

  useEffect(() => {
    load(1);
  }, [load, refreshKey]);

  const currency = meta?.currency ?? null;
  const when = (iso) => formatDateTime(iso, i18n.language);

  return (
    <section className="space-y-3">
      <p className="text-sm text-ink-2">{t("loyalty.history.intro")}</p>

      {error && (
        <div
          className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="py-10 text-center text-sm text-ink-2">{t("common.loading")}</p>
      ) : versions.length === 0 ? (
        <p className="card p-6 text-center text-sm text-ink-2">{t("loyalty.history.empty")}</p>
      ) : (
        <ul className="card divide-y divide-line">
          {versions.map((version) => (
            <li key={version.id}>
              <button
                type="button"
                onClick={() => setOpenId(version.id)}
                className="flex w-full flex-wrap items-start justify-between gap-3 px-4 py-3 text-start hover:bg-raised"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-semibold">
                    {t("loyalty.history.version", { number: version.version_number })}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        version.is_current ? "bg-accent-wash text-accent-text" : "bg-raised text-ink-2"
                      }`}
                    >
                      {labelFor("program_status", version.status)}
                    </span>
                  </p>
                  <p className="mt-0.5 text-sm text-ink">{versionSummary(version.summary, currency, t)}</p>
                  <VersionEvents version={version} />
                  {version.change_note && (
                    <p className="mt-0.5 text-sm italic text-ink-2">“{version.change_note}”</p>
                  )}
                </div>
                <div className="text-end text-xs text-ink-2">
                  <p dir="ltr">
                    {when(version.effective_from)} →{" "}
                    {version.effective_to ? when(version.effective_to) : t("loyalty.history.now")}
                  </p>
                  <p>{version.created_by?.name ?? t("loyalty.history.system")}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {meta && meta.last_page > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            className="btn-secondary"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1)}
          >
            {t("common.previous")}
          </button>
          <span className="text-ink-2">
            {page} / {meta.last_page}
          </span>
          <button
            type="button"
            className="btn-secondary"
            disabled={page >= meta.last_page || loading}
            onClick={() => load(page + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}

      {openId && (
        <VersionDetail
          programId={programId}
          id={openId}
          branchNames={branchNames}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

/** One version, read-only, with the settings that changed vs the previous version highlighted. */
function VersionDetail({ programId, id, branchNames, onClose }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchProgramVersion(programId, id)
      .then((result) => !cancelled && setData(result))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, t("loyalty.history.loadFailed"))));
    return () => {
      cancelled = true;
    };
  }, [programId, id, t]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const version = data?.version;
  const changed = new Set(version?.changed ?? []);

  const show = (key, settings) => {
    const value = settings?.[key];
    if (key === "service_points") {
      return (value ?? []).length
        ? value.map((row) => `${row.name ?? row.service_id}: ${row.points}`).join(", ")
        : t("loyalty.history.none");
    }
    if (value === null || value === undefined || value === "") {
      return key.startsWith("points_expire") || key === "reward_validity_days"
        ? t("loyalty.history.never")
        : "—";
    }
    if (key === "branch_id") {
      return branchNames[value] ?? (version?.branch?.id === value ? version.branch.name : null) ?? value;
    }
    if (key === "is_active") return value ? t("loyalty.history.on") : t("loyalty.history.off");
    if (key === "mode") return labelFor("loyalty_mode", value);
    if (key === "earn_on") return labelFor("loyalty_earn_on", value);
    if (key === "points_per_currency") return num(value);
    return String(value);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div role="dialog" aria-modal="true" className="card my-8 w-full max-w-2xl p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold">
              {version
                ? t("loyalty.history.version", { number: version.version_number })
                : t("common.loading")}
            </h2>
            {version && (
              <p className="text-sm text-ink-2">
                <span dir="ltr">
                  {formatDateTime(version.effective_from, i18n.language)} →{" "}
                  {version.effective_to
                    ? formatDateTime(version.effective_to, i18n.language)
                    : t("loyalty.history.now")}
                </span>
                {" · "}
                {version.created_by?.name ?? t("loyalty.history.system")}
              </p>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary px-2"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {error && <p className="field-error">{error}</p>}

        {version && (
          <>
            {version.change_note && (
              <p className="mb-3 rounded-lg bg-raised px-3 py-2 text-sm">“{version.change_note}”</p>
            )}
            <div className="mb-2">
              <VersionEvents version={version} />
            </div>
            <p className="mb-2 text-xs text-ink-2">
              {version.previous_version
                ? t("loyalty.history.changedHint", {
                    number: version.previous_version.version_number,
                    count: changed.size,
                  })
                : t("loyalty.history.firstVersion")}
            </p>
            <table className="w-full text-start text-sm">
              <thead className="border-b border-line text-xs uppercase tracking-wide text-ink-2">
                <tr>
                  <th className="py-2 pe-3">{t("loyalty.history.setting")}</th>
                  {version.previous_version && (
                    <th className="py-2 pe-3">
                      {t("loyalty.history.before", { number: version.previous_version.version_number })}
                    </th>
                  )}
                  <th className="py-2">{t("loyalty.history.value")}</th>
                </tr>
              </thead>
              <tbody>
                {SETTINGS.map((key) => {
                  const isChanged = changed.has(key);
                  return (
                    <tr
                      key={key}
                      className={`border-b border-line last:border-0 ${isChanged ? "bg-accent-wash" : ""}`}
                    >
                      <td className="py-2 pe-3 text-ink-2">
                        {t(`loyalty.history.fields.${key}`)}
                        {isChanged && (
                          <span className="ms-2 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            {t("loyalty.history.changed")}
                          </span>
                        )}
                      </td>
                      {version.previous_version && (
                        <td className={`py-2 pe-3 ${isChanged ? "text-ink-2 line-through" : "text-ink-2"}`}>
                          {show(key, version.previous_version.settings)}
                        </td>
                      )}
                      <td className={`py-2 ${isChanged ? "font-semibold text-accent-text" : ""}`}>
                        {show(key, version.settings)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
